import {useTranslation} from '@aboutyou/i18n';
import {
  useAccountOverviewRoute,
  useBasketRoute,
  useHistory,
  useWishlistRoute,
} from '@aboutyou/router';
import {COLORS, GenericIconName, Icon, Text} from '@aboutyou/ui-components';
import {
  useBasketContainsCampaignOrCouponItems,
  useBasketCount,
  useWishlistBubbleColour,
  useWishlistCount,
} from '@aboutyou/user';
import React, {FC} from 'react';
import styled from 'styled-components';
import {useAuthenticationPrompt} from '../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {useIsAuthenticated} from '../../features/Authentication/hooks/useIsAuthenticated';
import {useIsCouponWalletActive} from '../../features/Coupons/CouponWallet/hooks/useIsCouponWalletActive';
import {useSelectedDiscountColor} from '../Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {HeaderCoinsWalletButton} from '../Coins/HeaderCoinsWalletButton/HeaderCoinsWalletButton';
import {
  IconContainer,
  OffcanvasButton,
  StyledLink,
} from './Components/OffcanvasHeaderComponents';
import {OffcanvasHeaderCouponWalletButton} from './Components/OffcanvasHeaderCouponWalletButton';
import {useOffcanvasOpenState} from './useOffcanvasOpenState';
// eslint-disable-next-line monorepo/no-internal-import
import {WithBadge} from '@aboutyou/ui-components/src/components/atoms/Badge/WithBadge';

export const OffcanvasHeader: FC = () => {
  const {t} = useTranslation();
  const isCouponWalletActive = useIsCouponWalletActive();
  const accountOverviewRoute = useAccountOverviewRoute();
  const {isAuthenticated} = useIsAuthenticated();
  const history = useHistory();
  const {hide} = useOffcanvasOpenState();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const basketRoute = useBasketRoute();
  const wishlistRoute = useWishlistRoute();
  const {count: wishlistCount} = useWishlistCount();
  const wishlistBubbleColour = useWishlistBubbleColour();
  const basketCount = useBasketCount();
  const basketContainsCampaignItems = useBasketContainsCampaignOrCouponItems();
  const selectedDiscountColor = useSelectedDiscountColor();

  const handler = isAuthenticated
    ? () => history.push(accountOverviewRoute)
    : () =>
        showUserAuthenticationPrompt({
          context: 'mobile-offcanvas-login',
          needsCheckoutSession: false,
          onSuccess: () => {
            hide();
          },
        });

  return (
    <Wrapper>
      <OffcanvasButton
        data-testid="userNav"
        onClick={handler}
        aria-label={
          isAuthenticated
            ? t('Open your account page')
            : t('Open the login window')
        }
      >
        <Icon
          data-testid={isAuthenticated ? 'myAccount' : 'offCanvasLogin'}
          size="S"
          asset={GenericIconName.NavUser}
        />
        {!isCouponWalletActive && (
          <StyledLabel>
            {isAuthenticated ? t('Account') : t('Login')}
          </StyledLabel>
        )}
      </OffcanvasButton>
      <HeaderCoinsWalletButton />
      {isCouponWalletActive && (
        <OffcanvasHeaderCouponWalletButton
          badgeColor={selectedDiscountColor || COLORS['Alerts.Error.Primary']}
        />
      )}
      <StyledLink to={wishlistRoute} aria-label={t('Go to the wishlist page')}>
        <OffcanvasButton
          data-testid="OffcanvasToWishlist"
          aria-label={undefined}
        >
          <IconContainer>
            <WithBadge
              variant="rounded"
              offset={{
                top: 2,
                right: 0,
              }}
              badgeSize={7}
              badgeColor={selectedDiscountColor}
              displayBadgeWithoutCount={!!wishlistBubbleColour?.hex}
            >
              <Icon
                asset={GenericIconName.NavHeaderWishlist}
                fill={COLORS.Black}
                size="S"
              />
            </WithBadge>
            {Boolean(wishlistCount) && (
              <Text size="px9" weight={'mediumHeavy'}>
                {wishlistCount}
              </Text>
            )}
          </IconContainer>
          {!isCouponWalletActive && <StyledLabel>{t('Wishlist')}</StyledLabel>}
        </OffcanvasButton>
      </StyledLink>
      <StyledLink to={basketRoute} aria-label={t('Go to the basket page')}>
        <OffcanvasButton data-testid="OffcanvasToBasket" aria-label={undefined}>
          <IconContainer>
            <WithBadge
              variant="rounded"
              offset={{
                top: 2,
                right: 2,
              }}
              badgeSize={7}
              badgeColor={selectedDiscountColor}
              displayBadgeWithoutCount={basketContainsCampaignItems}
            >
              <Icon
                asset={GenericIconName.NavHeaderBasket}
                fill={COLORS.Black}
                size="S"
              />
            </WithBadge>
            {Boolean(basketCount) && (
              <Text size="px9" weight={'mediumHeavy'}>
                {basketCount}
              </Text>
            )}
          </IconContainer>
          {!isCouponWalletActive && <StyledLabel>{t('Basket')}</StyledLabel>}
        </OffcanvasButton>
      </StyledLink>
    </Wrapper>
  );
};

const StyledLabel = styled.span`
  max-width: 15ch;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  gap: 6px;
`;
